import * as React from "react"
import { Box, Button, Text, VStack } from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import { Link } from "react-router-dom"

import { ReactComponent as Logo } from "../assets/images/logo.svg"
import { Form } from "../components/Form"
import { Input } from "../components/Input"
import { AUTH_TOKEN, REFRESH_TOKEN, VERSION } from "../lib/config"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"
import { UserEntity } from "../lib/types/user"

export interface LoginResponse {
  token: string
  refresh_token: string
  user: {
    username: string
    email: string
    uuid: string
  }
}

export const Login = () => {
  useDocumentTitle("Login")

  const client = useQueryClient()
  const toast = useToast()
  const defaultValues = { email: "", password: "" }
  const form = useForm({ defaultValues })
  const [loading, setLoading] = React.useState(false)

  const { mutateAsync: login } = useMutation<LoginResponse, unknown, typeof defaultValues>((data) =>
    fetcher("jwt/login/", { body: JSON.stringify(data), method: "post" }),
  )
  const handleSubmit = async (data: typeof defaultValues) => {
    setLoading(true)
    try {
      const res = await login(data)
      if (!res) {
        toast({
          title: "Network error",
          description: "Please check your internet connection",
          status: "error",
        })
        return
      }
      if (res.user) {
        localStorage.setItem(AUTH_TOKEN, res.token)
        localStorage.setItem(REFRESH_TOKEN, res.refresh_token)
        const user: UserEntity = await fetcher("me/")
        if (!user) {
          toast({ description: "An error occured retrieving user info", status: "error" })
          return
        }
        const roles = await fetcher(`affiliations?user=${user.uuid}`)
        if (!user) {
          toast({ description: "An error occured retrieving user role", status: "error" })
          return
        }
        client.setQueryData("me/", () => user)
        client.setQueryData(`affiliations?user=${user.uuid}`, () => roles)
        toast({ description: "Successfully logged in!" })
      } else {
        toast({ description: "Invalid credentials", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
    setLoading(false)
  }

  return (
    <VStack spacing={8}>
      <VStack spacing={4}>
        <Box as={Logo} h={50} fill="gray.700" />
        <Text color="gray.500">FLEET MANAGEMENT SYSTEM</Text>
      </VStack>
      <Form {...form} onSubmit={handleSubmit}>
        <Input name="email" placeholder="Email" />
        <Input name="password" type="password" placeholder="Password" />
        <Button width="100%" type="submit" colorScheme="green" isDisabled={loading} isLoading={loading}>
          Login
        </Button>
        <Link to="forgot-password">Forgot password?</Link>
      </Form>
      <VStack spacing={4}>
        <Text color="gray.300">v{VERSION}</Text>
      </VStack>
    </VStack>
  )
}
